import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';

/**
 * Reusable "Reason for rejecting?" prompt — replaces `window.prompt`
 * everywhere in the admin UI. Caller passes a target description (e.g.
 * "Rayjens Code's purchase") + onSubmit; this renders a textarea, a
 * cancel button, and a red Reject button. Reason can be optional or
 * required at the caller's discretion.
 */
export interface RejectReasonFormData {
   /** Sentence shown above the textarea — e.g. "Reject Rayjens Code's purchase?" */
   prompt: string;
   /** Smaller helper text shown under the prompt. */
   hint?: string;
   /** Defaults to false — empty reason allowed. */
   required?: boolean;
   /** Defaults to "Reject". Customize for fulfill-reject vs application-reject etc. */
   submitLabel?: string;
   onSubmit: (reason: string) => void;
}

interface Props {
   data: RejectReasonFormData;
}

const RejectReasonForm = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const [reason, setReason] = useState('');
   const [showError, setShowError] = useState(false);
   const textareaRef = useRef<HTMLTextAreaElement>(null);

   useEffect(() => {
      const t = setTimeout(() => textareaRef.current?.focus(), 0);
      return () => clearTimeout(t);
   }, []);

   const submit = () => {
      const trimmed = reason.trim();
      if (data.required && !trimmed) {
         setShowError(true);
         return;
      }
      data.onSubmit(trimmed);
      handleClose();
   };

   return (
      <Wrapper>
         <Prompt>{data.prompt}</Prompt>
         {data.hint && <Hint>{data.hint}</Hint>}
         <Textarea
            ref={textareaRef}
            rows={3}
            value={reason}
            placeholder={
               data.required
                  ? 'Required — explain the rejection so the other party knows what happened.'
                  : 'Optional — short explanation if helpful.'
            }
            $error={showError}
            onChange={(e) => {
               setReason(e.target.value);
               if (showError && e.target.value.trim()) setShowError(false);
            }}
            onKeyDown={(e) => {
               if (e.key === 'Escape') handleClose();
            }}
         />
         {showError && (
            <ErrorText>A reason is required to reject.</ErrorText>
         )}
         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <div onClick={submit}>
               <Btn
                  title={data.submitLabel ?? 'Reject'}
                  type="normal"
                  size="small"
                  bgcolor={colors.DANGER}
                  textcolor={colors.WHITE}
               />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 420px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 10px;
   @media (max-width: 480px) { width: 100%; }
`;

const Prompt = styled.p`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const Hint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const Textarea = styled.textarea<{ $error?: boolean }>`
   padding: 9px 11px;
   border: 1px solid
      ${({ $error }) => ($error ? colors.DANGER : colors.GRAY12)};
   border-radius: 8px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   resize: vertical;
   min-height: 70px;
   &:focus {
      border-color: ${({ $error }) => ($error ? colors.DANGER : colors.PRIMARY)};
   }
`;

const ErrorText = styled.p`
   font-size: 11px;
   color: ${colors.DANGER};
   font-weight: 600;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

export default RejectReasonForm;
